import { createResource, createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { A } from '@solidjs/router';
import {
  fetchStrains,
  fetchLiveStock,
  formatPct,
  IRRADIATION_LABELS,
  type StrainRow,
  type LiveStockEntry,
  type IrradiationKind,
} from '@lib/supabase/queries';
import { fetchPortfolioStrainIds, type PortfolioSymptom } from '@lib/supabase/portfolio';
import { fetchWatchedStrainIds } from '@lib/supabase/watchlist';
import {
  fetchStrainSymptoms,
  setStrainSymptoms,
} from '@lib/supabase/strainSymptoms';
import { fetchSymptoms, suggestSymptom, type Symptom } from '@lib/supabase/symptoms';
import { fetchExcludedBrands } from '@lib/supabase/excludedBrands';
import { authStore } from '@stores/session';
import CollapsibleSection from '@components/ui/CollapsibleSection';
import DualRangeSlider from '@components/ui/DualRangeSlider';
import PageHead from '@components/seo/PageHead';
import FilterDrawer from '@components/layout/FilterDrawer';
import CardQuickActions from '@components/strain/CardQuickActions';
import RelationalSymptomEditor from '@components/strain/RelationalSymptomEditor';
import {
  summariseStock,
  strainNameWithThc,
  type StockSummary,
} from '@lib/strainDisplay';
import {
  urlFilters,
  urlString,
  urlBool,
  urlNumber,
  urlSet,
  urlStringSet,
} from '@lib/urlFilters';

type Lineage = 'sativa' | 'indica' | 'hybrid';
type LineageFilter = Lineage | 'unknown';
type Category = 'Flower' | 'Oil' | 'Vape' | 'Accessory';
type IrradiationFilter = IrradiationKind | 'unknown';
type FilterKey = 'search' | 'category' | 'lineage' | 'brand' | 'inStock' | 'thc' | 'cbd' | 'irradiation' | 'symptom';

const IRRADIATION_FILTER_OPTIONS: IrradiationFilter[] = ['none', 'e-beam', 'beta', 'gamma', 'unknown'];
const IRRADIATION_FILTER_LABELS: Record<IrradiationFilter, string> = {
  ...IRRADIATION_LABELS,
  unknown: 'Unknown / Not reported',
};

const LINEAGE_OPTIONS: LineageFilter[] = ['sativa', 'indica', 'hybrid', 'unknown'];
const LINEAGE_LABELS: Record<LineageFilter, string> = {
  sativa: 'Sativa',
  indica: 'Indica',
  hybrid: 'Hybrid',
  unknown: 'Unknown',
};
const CATEGORY_OPTIONS: Category[] = ['Flower', 'Oil', 'Vape', 'Accessory'];

// Validators for url-set filters. Pasting `?cat=DROP%20TABLE` into the
// URL bar shouldn't make it through to the filter logic; these
// predicates narrow incoming param values to the route's known enums.
const isCategory = (v: string): v is Category =>
  (CATEGORY_OPTIONS as string[]).includes(v);
const isLineageFilter = (v: string): v is LineageFilter =>
  (LINEAGE_OPTIONS as string[]).includes(v);
const isIrradiationFilter = (v: string): v is IrradiationFilter =>
  (IRRADIATION_FILTER_OPTIONS as string[]).includes(v);

export default function StrainListRoute(): JSX.Element {
  const [strains] = createResource(fetchStrains);
  const fullListTitle = () => 'Strain Database · Terp Track';
  const [stockEntries] = createResource(fetchLiveStock);

  // Bulk membership lookups so each card can render its quick-action
  // pre-state without a per-card query. Keyed on the auth user's id so
  // the resource refetches cleanly on sign-in/sign-out (returning an
  // empty Set when signed out, in which case CardQuickActions is
  // hidden entirely anyway).
  const [portfolioIds] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchPortfolioStrainIds() : new Set<string>()),
  );
  const [watchedIds] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchWatchedStrainIds() : new Set<string>()),
  );

  // Symptom tagging (signed-in only). Catalog feeds the picker; the
  // per-strain map drives each card's chips and the Symptoms filter.
  // Both mutate locally on edit so chips update without a refetch.
  const [symptomCatalog, { mutate: mutateCatalog }] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchSymptoms() : []),
  );
  const [strainSymptoms, { mutate: mutateStrainSymptoms }] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchStrainSymptoms() : new Map<string, PortfolioSymptom[]>()),
  );
  // Brands the user has hidden in Settings. Excluded from results by
  // default; a banner offers a one-tap "show anyway" override.
  const [excludedBrands] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchExcludedBrands() : new Set<string>()),
  );
  const [showExcluded, setShowExcluded] = createSignal(false);
  const symptomsFor = (strainId: string): PortfolioSymptom[] =>
    strainSymptoms()?.get(strainId) ?? [];

  function symptomsFromIds(ids: string[], extra?: Symptom[]): PortfolioSymptom[] {
    const catalog = symptomCatalog() ?? [];
    const pool = extra ? [...catalog, ...extra] : catalog;
    const wanted = new Set(ids);
    const out: PortfolioSymptom[] = [];
    const seen = new Set<string>();
    for (const s of pool) {
      if (!wanted.has(s.id) || seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({ id: s.id, name: s.name, status: s.status });
    }
    return out;
  }

  function writeStrainSymptoms(strainId: string, next: PortfolioSymptom[]) {
    mutateStrainSymptoms((prev) => {
      const m = new Map(prev ?? []);
      if (next.length) m.set(strainId, next);
      else m.delete(strainId);
      return m;
    });
  }

  async function applyStrainSymptoms(strainId: string, symptomIds: string[]) {
    await setStrainSymptoms(strainId, symptomIds);
    writeStrainSymptoms(strainId, symptomsFromIds(symptomIds));
  }

  async function suggestStrainSymptom(
    strainId: string,
    name: string,
    currentIds: string[],
  ): Promise<Symptom> {
    const created = await suggestSymptom(name);
    const nextIds = currentIds.includes(created.id)
      ? currentIds
      : [...currentIds, created.id];
    if (nextIds !== currentIds) await setStrainSymptoms(strainId, nextIds);
    mutateCatalog((prev) => {
      if (!prev) return [created];
      if (prev.some((s) => s.id === created.id)) return prev;
      return [...prev, created];
    });
    writeStrainSymptoms(strainId, symptomsFromIds(nextIds, [created]));
    return created;
  }

  // URL-backed filter state. Reading any accessor inside a memo or the
  // JSX is reactive against the search params; setters use replace:true
  // (configured inside the helper) so typing in the search box doesn't
  // pollute history with one entry per keystroke.
  const { params, set: setParams } = urlFilters();
  const [search, setSearch] = urlString(params, setParams, 'q');
  const [categories, setCategories] = urlSet<Category>(
    params,
    setParams,
    'cat',
    isCategory,
  );
  const [lineages, setLineages] = urlSet<LineageFilter>(
    params,
    setParams,
    'lin',
    isLineageFilter,
  );
  const [brands, setBrands] = urlStringSet(params, setParams, 'brand');
  const [inStockOnly, setInStockOnly] = urlBool(params, setParams, 'instock', false);
  const [irradiations, setIrradiations] = urlSet<IrradiationFilter>(
    params,
    setParams,
    'irr',
    isIrradiationFilter,
  );
  const [minThc, setMinThc] = urlNumber(params, setParams, 'thcmin');
  const [maxThc, setMaxThc] = urlNumber(params, setParams, 'thcmax');
  const [minCbd, setMinCbd] = urlNumber(params, setParams, 'cbdmin');
  const [maxCbd, setMaxCbd] = urlNumber(params, setParams, 'cbdmax');
  const [symptomFilter, setSymptomFilter] = urlStringSet(params, setParams, 'sym');
  function toggleSymptom(v: string) {
    const next = new Set(symptomFilter());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setSymptomFilter(next);
  }
  const availableSymptoms = createMemo(() => {
    const set = new Set<string>();
    for (const list of (strainSymptoms() ?? new Map()).values())
      for (const s of list as PortfolioSymptom[]) set.add(s.name);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  });

  // Local UI state — not part of the shareable filter set, so kept off
  // the URL. brandQuery is just a search-within-the-brand-list helper;
  // openOverrides tracks the user's explicit accordion toggles so a
  // re-render from a filter change doesn't collapse them.
  const [brandQuery, setBrandQuery] = createSignal('');
  // Explicit per-category open/close choices; categories not present
  // fall back to the auto-expand default (see sectionOpen below).
  const [openOverrides, setOpenOverrides] = createSignal<Map<Category, boolean>>(new Map());
  function toggleCategoryOpen(c: Category, open: boolean) {
    const next = new Map(openOverrides());
    next.set(c, open);
    setOpenOverrides(next);
  }

  const stockByStrain = createMemo(() => {
    const m = new Map<string, LiveStockEntry[]>();
    for (const e of stockEntries() ?? []) {
      const list = m.get(e.strain.id) ?? [];
      list.push(e);
      m.set(e.strain.id, list);
    }
    return m;
  });

  function matchesFilters(s: StrainRow, exclude?: FilterKey): boolean {
    const stockRows = stockByStrain().get(s.id) ?? [];
    if (exclude !== 'inStock' && inStockOnly()) {
      const inStock = stockRows.some((r) => r.status === 'In Stock' || r.status === 'Low Stock');
      if (!inStock) return false;
    }
    if (exclude !== 'irradiation' && irradiations().size > 0) {
      const ir = irradiations();
      if (stockRows.length === 0) {
        if (!ir.has('unknown')) return false;
      } else {
        const hit = stockRows.some((r) => ir.has((r.irradiation ?? 'unknown') as IrradiationFilter));
        if (!hit) return false;
      }
    }
    if (exclude !== 'thc' && (minThc() != null || maxThc() != null)) {
      const hit = stockRows.some(
        (r) =>
          r.thc_pct_advertised != null &&
          (minThc() == null || r.thc_pct_advertised >= minThc()!) &&
          (maxThc() == null || r.thc_pct_advertised <= maxThc()!),
      );
      if (!hit) return false;
    }
    if (exclude !== 'cbd' && (minCbd() != null || maxCbd() != null)) {
      const hit = stockRows.some(
        (r) =>
          r.cbd_pct_advertised != null &&
          (minCbd() == null || r.cbd_pct_advertised >= minCbd()!) &&
          (maxCbd() == null || r.cbd_pct_advertised <= maxCbd()!),
      );
      if (!hit) return false;
    }
    if (exclude !== 'search') {
      const q = search().trim().toLowerCase();
      if (q) {
        const hay = `${s.brand} ${s.name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
    }
    if (exclude !== 'category' && categories().size > 0) {
      if (!categories().has(s.category as Category)) return false;
    }
    if (exclude !== 'lineage' && lineages().size > 0) {
      const li = lineages();
      if (s.lineage == null) {
        if (!li.has('unknown')) return false;
      } else {
        if (!li.has(s.lineage as Lineage)) return false;
      }
    }
    if (exclude !== 'brand' && brands().size > 0 && !brands().has(s.brand)) return false;
    if (exclude !== 'symptom' && symptomFilter().size > 0) {
      // OR semantics over the viewer's own tags for this strain.
      const tags = symptomsFor(s.id).map((t) => t.name);
      if (!tags.some((t) => symptomFilter().has(t))) return false;
    }
    return true;
  }

  function toggleCategory(v: Category) {
    const next = new Set(categories());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setCategories(next);
  }
  function toggleLineage(v: LineageFilter) {
    const next = new Set(lineages());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setLineages(next);
  }
  function toggleBrand(v: string) {
    const next = new Set(brands());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setBrands(next);
  }

  function resetAll() {
    setSearch('');
    setCategories(new Set<Category>());
    setLineages(new Set<LineageFilter>());
    setBrands(new Set<string>());
    setBrandQuery('');
    setInStockOnly(false);
    setIrradiations(new Set<IrradiationFilter>());
    setMinThc(null);
    setMaxThc(null);
    setMinCbd(null);
    setMaxCbd(null);
    setSymptomFilter(new Set<string>());
  }

  function toggleIrradiation(v: IrradiationFilter) {
    const next = new Set(irradiations());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setIrradiations(next);
  }

  // Cross-filter option subsets — Brand list narrows as you pick Type,
  // and vice versa, so you only see combinations that can actually
  // produce results.
  const subsetForCategory = createMemo(() =>
    (strains() ?? []).filter((s) => matchesFilters(s, 'category')),
  );
  const subsetForLineage = createMemo(() =>
    (strains() ?? []).filter((s) => matchesFilters(s, 'lineage')),
  );
  const subsetForBrand = createMemo(() =>
    (strains() ?? []).filter((s) => matchesFilters(s, 'brand')),
  );

  const availableCategories = createMemo(() => {
    const set = new Set<Category>();
    for (const s of subsetForCategory()) set.add(s.category as Category);
    for (const c of categories()) set.add(c);
    return CATEGORY_OPTIONS.filter((c) => set.has(c));
  });

  const availableLineages = createMemo(() => {
    const set = new Set<LineageFilter>();
    for (const s of subsetForLineage()) set.add((s.lineage ?? 'unknown') as LineageFilter);
    for (const l of lineages()) set.add(l);
    return LINEAGE_OPTIONS.filter((l) => set.has(l));
  });

  const availableBrands = createMemo(() => {
    const set = new Set<string>();
    for (const s of subsetForBrand()) set.add(s.brand);
    for (const b of brands()) set.add(b);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  });

  const filteredBrandOptions = createMemo(() => {
    const q = brandQuery().toLowerCase().trim();
    const all = availableBrands();
    if (!q) return all;
    return all.filter((b) => b.toLowerCase().includes(q));
  });

  const filtered = createMemo(() => (strains() ?? []).filter((s) => matchesFilters(s)));

  // Drop excluded-brand strains unless the user has opted to show them.
  const visibleStrains = createMemo(() => {
    const ex = excludedBrands();
    const base = filtered();
    if (showExcluded() || !ex || ex.size === 0) return base;
    return base.filter((s) => !ex.has(s.brand));
  });
  const excludedInResults = createMemo(() => {
    const ex = excludedBrands();
    if (!ex || ex.size === 0) return 0;
    return filtered().filter((s) => ex.has(s.brand)).length;
  });

  // One card per strain (potency is part of strain identity now), with
  // its stock rolled up into a single summary. Strains with no stock data
  // get a suffix-less card.
  type CatalogueCard = { strain: StrainRow; summary: StockSummary | null };

  const catalogueCards = createMemo<CatalogueCard[]>(() =>
    visibleStrains().map((s) => ({
      strain: s,
      summary: summariseStock(stockByStrain().get(s.id) ?? []),
    })),
  );

  const groupedByCategory = createMemo(() => {
    const buckets = new Map<Category, CatalogueCard[]>();
    for (const card of catalogueCards()) {
      const cat = card.strain.category as Category;
      const list = buckets.get(cat) ?? [];
      list.push(card);
      buckets.set(cat, list);
    }
    return CATEGORY_OPTIONS
      .filter((c) => (buckets.get(c)?.length ?? 0) > 0)
      .map((c) => ({ category: c, rows: buckets.get(c) ?? [] }));
  });

  // Auto-expand: open the sections by default when the filter narrows to
  // 1–2 categories. An explicit user toggle (openOverrides) always wins.
  const autoExpand = createMemo(() => groupedByCategory().length <= 2);
  function sectionOpen(c: Category): boolean {
    const explicit = openOverrides().get(c);
    return explicit !== undefined ? explicit : autoExpand();
  }

  const activeFilterCount = createMemo(() => {
    let n = 0;
    if (search()) n++;
    if (categories().size) n++;
    if (lineages().size) n++;
    if (brands().size) n++;
    if (inStockOnly()) n++;
    if (irradiations().size) n++;
    if (minThc() != null || maxThc() != null) n++;
    if (minCbd() != null || maxCbd() != null) n++;
    if (symptomFilter().size) n++;
    return n;
  });

  return (
    <section class="px-4 sm:px-6 py-10 max-w-7xl mx-auto grid lg:grid-cols-[260px_1fr] gap-8">
      <PageHead
        title={fullListTitle()}
        description="Browse every strain Terp Track has indexed &ndash; flower, oil, vape, and accessories from UK pharmacies. Filter by THC, CBD, lineage, irradiation, and in-stock status."
        canonical="/strains"
      />
      <FilterDrawer activeCount={activeFilterCount}>
        <div class="flex items-center justify-between">
          <h2 class="font-display text-lg font-bold">Filters</h2>
          <Show when={activeFilterCount() > 0}>
            <button
              type="button"
              onClick={resetAll}
              class="text-xs text-brand-moss hover:underline"
            >
              Reset all
            </button>
          </Show>
        </div>

        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Search</span>
          <input
            type="search" name="search"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            placeholder="Strain or brand…"
            class="rounded-md border border-brand-hex px-3 py-2 text-sm"
          />
        </label>

        <label class="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={inStockOnly()}
            onChange={(e) => setInStockOnly(e.currentTarget.checked)}
            name="filter" class="accent-brand-moss"
          />
          <span class="font-medium">In stock only</span>
        </label>

        <fieldset>
          <legend class="text-sm font-medium mb-1">Type</legend>
          <For
            each={availableCategories()}
            fallback={<p class="text-xs text-brand-green/50">No options match other filters.</p>}
          >
            {(c) => (
              <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={categories().has(c)}
                  onChange={() => toggleCategory(c)}
                  name="filter" class="accent-brand-moss"
                />
                {c}
              </label>
            )}
          </For>
        </fieldset>

        <fieldset>
          <div class="flex items-center justify-between mb-1">
            <legend class="text-sm font-medium">THC %</legend>
            <Show when={minThc() != null || maxThc() != null}>
              <button type="button" onClick={() => { setMinThc(null); setMaxThc(null); }} class="text-xs text-brand-moss hover:underline">any</button>
            </Show>
          </div>
          <DualRangeSlider
            min={0}
            max={35}
            step={1}
            value={{ min: minThc() ?? 0, max: maxThc() ?? 35 }}
            onInput={(v) => {
              setMinThc(v.min);
              setMaxThc(v.max);
            }}
            format={(v) => `${v}%`}
          />
        </fieldset>

        <fieldset>
          <div class="flex items-center justify-between mb-1">
            <legend class="text-sm font-medium">CBD %</legend>
            <Show when={minCbd() != null || maxCbd() != null}>
              <button type="button" onClick={() => { setMinCbd(null); setMaxCbd(null); }} class="text-xs text-brand-moss hover:underline">any</button>
            </Show>
          </div>
          <DualRangeSlider
            min={0}
            max={25}
            step={0.1}
            value={{ min: minCbd() ?? 0, max: maxCbd() ?? 25 }}
            onInput={(v) => {
              setMinCbd(v.min);
              setMaxCbd(v.max);
            }}
            format={(v) => `${v.toFixed(1)}%`}
          />
        </fieldset>

        <fieldset>
          <legend class="text-sm font-medium mb-1">Irradiation</legend>
          <For each={IRRADIATION_FILTER_OPTIONS}>
            {(k) => (
              <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={irradiations().has(k)}
                  onChange={() => toggleIrradiation(k)}
                  name="filter" class="accent-brand-moss"
                />
                {IRRADIATION_FILTER_LABELS[k]}
              </label>
            )}
          </For>
        </fieldset>

        <fieldset>
          <legend class="text-sm font-medium mb-1">Profile</legend>
          <For
            each={availableLineages()}
            fallback={<p class="text-xs text-brand-green/50">No options match other filters.</p>}
          >
            {(l) => (
              <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={lineages().has(l)}
                  onChange={() => toggleLineage(l)}
                  name="filter" class="accent-brand-moss"
                />
                {LINEAGE_LABELS[l]}
              </label>
            )}
          </For>
        </fieldset>

        <fieldset>
          <div class="flex items-center justify-between mb-1">
            <legend class="text-sm font-medium">Brand</legend>
            <Show when={brands().size > 0}>
              <button
                type="button"
                onClick={() => setBrands(new Set<string>())}
                class="text-xs text-brand-moss hover:underline"
              >
                clear
              </button>
            </Show>
          </div>
          <input
            type="search" name="search"
            value={brandQuery()}
            onInput={(e) => setBrandQuery(e.currentTarget.value)}
            placeholder="Search brand…"
            class="w-full rounded-md border border-brand-hex px-2 py-1 text-xs mb-1"
          />
          <div class="max-h-56 overflow-y-auto pr-1">
            <For
              each={filteredBrandOptions()}
              fallback={<p class="text-xs text-brand-green/50 py-1">No brands match other filters.</p>}
            >
              {(b) => (
                <label class="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={brands().has(b)}
                    onChange={() => toggleBrand(b)}
                    name="filter" class="accent-brand-moss"
                  />
                  <span class="truncate">{b}</span>
                </label>
              )}
            </For>
          </div>
        </fieldset>

        <Show when={availableSymptoms().length > 0}>
          <fieldset>
            <div class="flex items-center justify-between mb-1">
              <legend class="text-sm font-medium">Symptoms</legend>
              <Show when={symptomFilter().size > 0}>
                <button
                  type="button"
                  onClick={() => setSymptomFilter(new Set<string>())}
                  class="text-xs text-brand-moss hover:underline"
                >
                  clear
                </button>
              </Show>
            </div>
            <For each={availableSymptoms()}>
              {(s) => (
                <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={symptomFilter().has(s)}
                    onChange={() => toggleSymptom(s)}
                    name="filter" class="accent-brand-moss"
                  />
                  {s}
                </label>
              )}
            </For>
          </fieldset>
        </Show>
      </FilterDrawer>

      <div>
        <div class="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 class="font-display text-3xl font-bold">Strain Database</h1>
            <p class="text-sm text-brand-green/80 mt-1">
              {visibleStrains().length} strain{visibleStrains().length === 1 ? '' : 's'}
              {activeFilterCount() > 0 &&
                ` · ${activeFilterCount()} filter${activeFilterCount() === 1 ? '' : 's'} active`}
            </p>
          </div>
          <A
            href="/strains/top-rated"
            class="text-sm rounded-full border border-brand-hex bg-white px-4 py-2 hover:bg-brand-hex/40 transition-colors"
          >
            ★ Top Rated
          </A>
        </div>

        <Show when={strains.error}>
          <p class="mt-6 text-sm text-red-700" role="alert">
            Failed to load strains: {(strains.error as Error).message}
          </p>
        </Show>

        <Show when={excludedInResults() > 0}>
          <div class="mt-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <span>
              {excludedInResults()} result{excludedInResults() === 1 ? '' : 's'} from brands you've
              hidden {showExcluded() ? 'are shown below' : 'are hidden'}.
            </span>
            <button
              type="button"
              onClick={() => setShowExcluded(!showExcluded())}
              class="shrink-0 rounded-full border border-amber-400 px-3 py-1 font-medium cursor-pointer hover:bg-amber-100 transition-colors"
            >
              {showExcluded() ? 'Hide them' : 'Show anyway'}
            </button>
          </div>
        </Show>

        <Show when={!strains.loading} fallback={<p class="mt-6 text-sm">Loading…</p>}>
          <Show
            when={groupedByCategory().length > 0}
            fallback={
              <p class="mt-8 text-sm text-brand-green/80">
                No matching strains. Try relaxing a filter.
              </p>
            }
          >
            <div class="mt-6 space-y-3">
              <For each={groupedByCategory()}>
                {(section) => (
                  <CollapsibleSection
                    title={section.category}
                    count={section.rows.length}
                    open={sectionOpen(section.category)}
                    onToggle={(o) => toggleCategoryOpen(section.category, o)}
                  >
                    <ul class="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <For each={section.rows}>
                        {(card) => (
                          <li class="rounded-xl border border-brand-hex bg-white p-4 hover:border-brand-moss transition-colors">
                            {/* The link wraps the strain content but
                                stops short of the action row — the
                                CardQuickActions buttons are siblings
                                so clicking them never navigates. */}
                            <A href={`/strains/${card.strain.slug}`} class="block">
                              <p class="text-xs text-brand-green/80 uppercase tracking-wide">
                                {card.strain.brand}
                              </p>
                              <p class="font-display text-lg font-semibold">
                                {strainNameWithThc(card.strain.name, card.summary?.thc ?? null)}
                              </p>
                              <div class="flex items-center gap-2 flex-wrap mt-1 text-xs text-brand-green/80">
                                <Show when={card.summary?.thc != null}>
                                  <span>THC {formatPct(card.summary!.thc, card.summary!.thcUpper)}</span>
                                </Show>
                                <Show when={card.summary?.cbd != null}>
                                  <span>· CBD {formatPct(card.summary!.cbd, card.summary!.cbdUpper)}</span>
                                </Show>
                              </div>
                              <div class="flex items-center gap-1 flex-wrap mt-2">
                                <Show when={card.strain.lineage}>
                                  <span class="text-[10px] px-2 py-0.5 rounded-full bg-brand-hex text-brand-green capitalize">
                                    {card.strain.lineage}
                                  </span>
                                </Show>
                                <For each={card.summary?.irradiations ?? []}>
                                  {(k) => (
                                    <span class="text-[10px] px-2 py-0.5 rounded-full bg-brand-moss/10 text-brand-green">
                                      {IRRADIATION_LABELS[k]}
                                    </span>
                                  )}
                                </For>
                                <Show when={card.summary == null}>
                                  <span class="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
                                    No stock data
                                  </span>
                                </Show>
                              </div>
                            </A>
                            <CardQuickActions
                              strainId={card.strain.id}
                              initialInPortfolio={portfolioIds()?.has(card.strain.id) ?? false}
                              initialInWatchlist={watchedIds()?.has(card.strain.id) ?? false}
                            />
                            <Show when={authStore.user()}>
                              <div class="mt-2">
                                <RelationalSymptomEditor
                                  current={symptomsFor(card.strain.id)}
                                  catalog={symptomCatalog() ?? []}
                                  onChange={(ids) => applyStrainSymptoms(card.strain.id, ids)}
                                  onSuggest={(name, currentIds) =>
                                    suggestStrainSymptom(card.strain.id, name, currentIds)
                                  }
                                />
                              </div>
                            </Show>
                          </li>
                        )}
                      </For>
                    </ul>
                  </CollapsibleSection>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </div>
    </section>
  );
}
